import { Select } from '@/components/selects';
import { Form, Formik, FormikProps } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Input, TextArea } from '../../../../../../components/inputs';
import { DataSourceSFTPConfig, TDataSource } from '../../../../../../models/dataSource';
import { FileStepContentWrapper } from '../../file/FileStepContentWrapper';
import { AUTH_METHODS, AUTH_METHODS_OPTIONS } from './constants';
export type SFTPFormModel = Partial<TDataSource<DataSourceSFTPConfig>>;

type SFTPNameAndBucketStepProps = {
  formRef: React.RefObject<FormikProps<SFTPFormModel>>;
  onSubmit: (form: SFTPFormModel) => void;
  dataSource?: Partial<TDataSource<DataSourceSFTPConfig>>;
};

export const SFTPNameAndBucketStep: FC<SFTPNameAndBucketStepProps> = ({ formRef, onSubmit, dataSource }) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({
      host: Yup.string().required(t('data_source_edit:validations.required')),
      username: Yup.string().required(t('data_source_edit:validations.required')),
      path: Yup.string().required(t('data_source_edit:validations.required')),
      authMethod: Yup.object({
        id: Yup.string()
          .required(t('data_source_edit:validations.required'))
          .oneOf(Object.values(AUTH_METHODS).map((method) => method)),
        label: Yup.string().required(),
      }).required(t('data_source_edit:validations.required')),
      password: Yup.string().when(['..authMethod.id'], {
        is: (id: string) => id === AUTH_METHODS.password,
        then: (schema) => schema.required(t('data_source_edit:validations.required')),
        otherwise: (schema) => schema.notRequired(),
      }),
      privateKey: Yup.string().when(['..authMethod.id'], {
        is: (id: string) => id === AUTH_METHODS.privateKey,
        then: (schema) => schema.required(t('data_source_edit:validations.required')),
        otherwise: (schema) => schema.notRequired(),
      }),
      passphrase: Yup.string(),
      port: Yup.number(),
    }),
  });

  return (
    <Formik
      innerRef={formRef}
      initialValues={
        dataSource
          ? {
              ...dataSource,
              name: '',
            }
          : {
              name: '',
              configuration: {
                host: '',
                username: '',
                path: '',
                authMethod: undefined,
                password: undefined,
                privateKey: undefined,
                passphrase: undefined,
                port: undefined,
              },
            }
      }
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ values, errors, handleChange, setFieldError, setFieldValue }) => {
        return (
          <Form className='max-h-[550px] overflow-y-auto'>
            <FileStepContentWrapper>
              <Input
                name='name'
                id='name'
                type='text'
                label={t('data_source_edit:code.source_details.source_name')}
                value={values?.name}
                placeholder={t('data_source_edit:sftp.source_name_placeholder')}
                onChange={(e) => {
                  setFieldError('name', undefined);
                  handleChange('name')(e.target.value);
                }}
                error={errors?.name}
              />
            </FileStepContentWrapper>
            <label className='block text-sm ps-6 font-medium text-left text-gray-700 capitalize-first py-2 mt-4'>
              {t('data_source_edit:sftp.requiredFields')}
            </label>
            <FileStepContentWrapper className='gap-1.5 flex flex-col max-h-full'>
              <Input
                name='configuration.host'
                id='configuration.host'
                type='text'
                label={t('data_source_edit:sftp.host')}
                value={values?.configuration.host}
                placeholder={t('data_source_edit:sftp.insertTextHere')}
                onChange={(e) => {
                  setFieldError('configuration.host', undefined);
                  handleChange('configuration.host')(e.target.value);
                }}
                error={errors?.configuration?.host}
              />
              <Input
                name='configuration.username'
                id='configuration.username'
                type='text'
                label={t('data_source_edit:sftp.username')}
                value={values?.configuration.username}
                placeholder={t('data_source_edit:sftp.insertTextHere')}
                onChange={(e) => {
                  setFieldError('configuration.username', undefined);
                  handleChange('configuration.username')(e.target.value);
                }}
                error={errors?.configuration?.username}
              />
              <Input
                name='configuration.path'
                id='configuration.path'
                type='text'
                label={t('data_source_edit:sftp.path')}
                value={values?.configuration.path}
                placeholder={t('data_source_edit:sftp.insertTextHere')}
                onChange={(e) => {
                  setFieldError('configuration.path', undefined);
                  handleChange('configuration.path')(e.target.value);
                }}
                error={errors?.configuration?.path}
              />
              <label className={'block text-sm font-medium text-start text-gray-700 py-2'}>{t('data_source_edit:sftp.passwordOrPrivateKey')}</label>
              <Select
                value={values.configuration.authMethod}
                items={AUTH_METHODS_OPTIONS.map((option) => ({
                  ...option,
                  label: t(`data_source_edit:sftp.${option.label as keyof typeof AUTH_METHODS}`),
                }))}
                onChange={(item) => {
                  setFieldValue('configuration.password', undefined);
                  setFieldValue('configuration.privateKey', undefined);
                  setFieldValue('configuration.authMethod', item);
                }}
                className='w-full'
                placeholder={t('data_source_edit:sftp.privateKey_placeholder')}
              />
              {values.configuration.authMethod?.id === AUTH_METHODS.password && (
                <Input
                  name='configuration.password'
                  id='configuration.password'
                  type='text'
                  label={t('data_source_edit:sftp.password')}
                  labelClassName='capitalize'
                  value={values?.configuration.password}
                  placeholder={t('data_source_edit:sftp.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.password', undefined);
                    handleChange('configuration.password')(e.target.value);
                  }}
                  error={errors?.configuration?.password}
                />
              )}
              {values.configuration.authMethod?.id === AUTH_METHODS.privateKey && (
                <>
                  <TextArea
                    name='configuration.privateKey'
                    id='configuration.privateKey'
                    label={t('data_source_edit:sftp.privateKey')}
                    value={values?.configuration.privateKey}
                    placeholder={t('data_source_edit:sftp.insertTextHere')}
                    onChange={(e) => {
                      setFieldError('configuration.privateKey', undefined);
                      handleChange('configuration.privateKey')(e.target.value);
                    }}
                    error={errors?.configuration?.privateKey}
                    className='placeholder:whitespace-pre-wrap'
                    wrapperClassName='m-0 text-start'
                  />
                  <Input
                    name='configuration.passphrase'
                    id='configuration.passphrase'
                    type='text'
                    label={t('data_source_edit:sftp.passphrase')}
                    value={values?.configuration.passphrase}
                    placeholder={t('data_source_edit:sftp.passphrase_placeholder')}
                    onChange={(e) => {
                      setFieldError('configuration.passphrase', undefined);
                      handleChange('configuration.passphrase')(e.target.value);
                    }}
                    error={errors?.configuration?.passphrase}
                  />
                </>
              )}
              <Input
                name='configuration.port'
                id='configuration.port'
                type='number'
                label={t('data_source_edit:sftp.port')}
                value={values?.configuration.port}
                placeholder={t('data_source_edit:sftp.port_placeholder')}
                onChange={(e) => {
                  setFieldError('configuration.port', undefined);
                  handleChange('configuration.port')(e.target.value);
                }}
                error={errors?.configuration?.port}
              />
            </FileStepContentWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
